import { useEffect, useState } from 'react';
import { LogOut, Loader2, AlertTriangle, Truck, Wallet, Download, TrendingUp, TrendingDown } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { apiClient, extractErrorMessage, getStoredToken } from '@/lib/api';
import { useAuth } from '@/lib/auth';

interface DashboardData {
  investor: { id: string; code: string | null; fullNameEn: string | null; phone: string | null };
  vehicles: { id: string; plate: string | null; type: string | null; brand: string | null; model: string | null;
              monthRevenue: number; monthExpenses: number; monthNet: number; monthReturn: number | null }[];
  monthAggregate: { revenue: number; expenses: number; net: number; vehicles: number };
  monthlyTrend: { month: string; net: number }[];
  publishedAt: string;
}

const API_BASE = (import.meta.env.VITE_API_URL as string) || 'https://api.jasrhub.com';
const fmtSar = (n: number) => 'SAR ' + n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

function previousMonth(): string {
  const d = new Date(); d.setMonth(d.getMonth() - 1);
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0');
}

export default function InvestorSelfView() {
  const { logout } = useAuth();
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statementMonth, setStatementMonth] = useState<string>(previousMonth());
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    apiClient.get<DashboardData>('/api/v1/investors/me/dashboard')
      .then((r) => setData(r.data))
      .catch((err) => setError(extractErrorMessage(err)))
      .finally(() => setLoading(false));
  }, []);

  async function downloadStatement() {
    setDownloading(true);
    try {
      const jwt = getStoredToken();
      const res = await fetch(API_BASE + '/api/v1/investors/me/statement/pdf?month=' + statementMonth, {
        headers: jwt ? { Authorization: 'Bearer ' + jwt } : {},
      });
      if (!res.ok) { window.alert('Statement download failed: ' + res.statusText); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'investor_statement_' + statementMonth + '.pdf';
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } finally {
      setDownloading(false);
    }
  }

  if (loading) return <div className="min-h-screen bg-background flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  if (error || !data) return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-4 p-6">
      <AlertTriangle className="h-8 w-8 text-amber-500" />
      <div className="text-sm text-muted-foreground text-center max-w-sm">{error ?? 'No investor record linked to your account'}</div>
      <Button variant="outline" onClick={logout} className="gap-2"><LogOut className="h-4 w-4" /> Sign out</Button>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card">
        <div className="max-w-md mx-auto px-4 py-3 flex items-center justify-between">
          <div className="min-w-0">
            <div className="text-sm font-medium truncate">{data.investor.fullNameEn ?? data.investor.code ?? '—'}</div>
            <div className="text-xs text-muted-foreground">{data.monthAggregate.vehicles} vehicle{data.monthAggregate.vehicles === 1 ? '' : 's'}</div>
          </div>
          <Button variant="ghost" size="sm" onClick={logout} className="gap-2"><LogOut className="h-4 w-4" /> Sign out</Button>
        </div>
      </header>

      <main className="max-w-md mx-auto px-4 py-4 space-y-4">
        {/* Portfolio summary */}
        <div className={'rounded-xl border p-5 text-center ' + (data.monthAggregate.net >= 0 ? 'border-emerald-500/40 bg-emerald-500/5' : 'border-rose-500/40 bg-rose-500/5')}>
          <div className="text-xs uppercase tracking-wider text-muted-foreground">This month — net to investor</div>
          <div className={'text-4xl font-display mt-2 inline-flex items-center gap-2 ' + (data.monthAggregate.net >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400')}>
            {data.monthAggregate.net >= 0 ? <TrendingUp className="h-7 w-7" /> : <TrendingDown className="h-7 w-7" />}
            {fmtSar(data.monthAggregate.net)}
          </div>
          <div className="text-xs text-muted-foreground mt-2">
            Revenue {fmtSar(data.monthAggregate.revenue)} − Expenses {fmtSar(data.monthAggregate.expenses)}
          </div>
        </div>

        {/* Vehicles list */}
        <div className="space-y-2">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">Your vehicles</div>
          {data.vehicles.length === 0 ? (
            <div className="rounded-lg border border-border p-6 text-center text-sm text-muted-foreground">No vehicles yet.</div>
          ) : (
            <div className="space-y-1">
              {data.vehicles.map((v) => (
                <div key={v.id} className="rounded-lg border border-border p-3">
                  <div className="flex items-baseline justify-between gap-2">
                    <div className="font-mono font-medium text-sm">{v.plate ?? v.id.slice(0, 8)}</div>
                    <div className={'font-mono text-sm ' + (v.monthNet >= 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-rose-700 dark:text-rose-400')}>
                      {fmtSar(v.monthNet)}
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5 inline-flex items-center gap-1.5"><Truck className="h-3 w-3" /> {v.brand ?? ''} {v.model ?? ''}</div>
                  <div className="text-[11px] text-muted-foreground mt-1 flex flex-wrap gap-x-3">
                    <span>Revenue {fmtSar(v.monthRevenue)}</span>
                    <span>Expenses {fmtSar(v.monthExpenses)}</span>
                    {v.monthReturn !== null && <span>Return {v.monthReturn.toFixed(1)}%</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Monthly trend */}
        {data.monthlyTrend.length > 0 && (
          <div className="space-y-2">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">Last 12 months</div>
            <div className="rounded-lg border border-border p-3">
              <div className="flex items-end gap-1.5 h-24">
                {data.monthlyTrend.map((m) => {
                  const max = Math.max(...data.monthlyTrend.map((x) => Math.abs(x.net)));
                  const h = max > 0 ? Math.max(2, (Math.abs(m.net) / max) * 90) : 2;
                  return (
                    <div key={m.month} className="flex-1 flex flex-col items-center justify-end gap-1">
                      <div className={'w-full rounded-t ' + (m.net >= 0 ? 'bg-emerald-500' : 'bg-rose-500')} style={{ height: h + '%' }} title={m.month + ': ' + fmtSar(m.net)} />
                    </div>
                  );
                })}
              </div>
              <div className="grid mt-2" style={{ gridTemplateColumns: 'repeat(' + data.monthlyTrend.length + ', minmax(0, 1fr))' }}>
                {data.monthlyTrend.map((m) => (
                  <div key={m.month} className="text-[9px] text-muted-foreground text-center font-mono">{m.month.slice(5)}</div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Statement download */}
        <div className="space-y-2">
          <div className="text-xs uppercase tracking-wider text-muted-foreground inline-flex items-center gap-1.5"><Wallet className="h-3 w-3" /> Monthly statement</div>
          <div className="rounded-lg border border-border p-3 flex items-end gap-2">
            <div className="flex-1 space-y-1">
              <Label htmlFor="m">Month</Label>
              <Input id="m" type="month" value={statementMonth} onChange={(e) => setStatementMonth(e.target.value)} />
            </div>
            <Button onClick={downloadStatement} disabled={downloading} className="gap-2 h-11">
              {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} PDF
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
}
